import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import {
  Card, CardHeader, CardBody, Button, Input, Progress, Alert,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { uploadResourceFiles, clearResourceUploads } from 'redux/ResourceFiles/ResourceFilesAction';
import {
  ALLOWED_RESOURCE_EXTENSIONS,
  validateResourceFile,
  formatFileSize,
} from 'util/resourceFiles';

const ACCEPT = ALLOWED_RESOURCE_EXTENSIONS.map((e) => `.${e}`).join(',');

const fileKey = (file) => `${file.name}-${file.size}`;

const ResourceFileUploader = ({ uploads, uploadResourceFiles, clearResourceUploads }) => {
  // Each entry is { file, description } so descriptions stay attached to their
  // file even when other files are removed from the list.
  const [selected, setSelected] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    setSelected((prev) => {
      const existing = new Set(prev.map((entry) => fileKey(entry.file)));
      const added = incoming
        .filter((file) => !existing.has(fileKey(file)))
        .map((file) => ({ file, description: '' }));
      return [...prev, ...added];
    });
  };

  const removeFile = (index) => {
    setSelected((prev) => prev.filter((_, i) => i !== index));
  };

  const setDescription = (index, description) => {
    setSelected((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, description } : entry))
    );
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(e.dataTransfer.files);
  };

  const handleUpload = async () => {
    if (selected.length === 0) return;
    setUploading(true);
    clearResourceUploads();

    const results = await uploadResourceFiles(
      selected.map((entry) => entry.file),
      {
        category: 'anki-deck',
        visibility: 'lead-magnet',
        descriptions: selected.map((entry) => entry.description),
      }
    );

    setUploading(false);

    const failed = results.filter((r) => !r.success);
    const succeeded = results.length - failed.length;

    if (failed.length === 0) {
      Swal.fire({
        title: 'Uploaded',
        text: `${succeeded} file${succeeded === 1 ? '' : 's'} uploaded successfully.`,
        icon: 'success',
        timer: 2500,
      });
      setSelected([]);
      if (inputRef.current) inputRef.current.value = '';
    } else {
      Swal.fire({
        title: 'Some uploads failed',
        html: `${succeeded} succeeded, ${failed.length} failed:<br/><br/>${failed
          .map((f) => `<small>${f.fileName}: ${f.error}</small>`)
          .join('<br/>')}`,
        icon: 'warning',
      });
      // Keep only the files that failed so they can be retried
      const failedNames = new Set(failed.map((f) => f.fileName));
      setSelected((prev) => prev.filter((entry) => failedNames.has(entry.file.name)));
    }
  };

  const uploadEntries = Object.entries(uploads || {});

  return (
    <Card className="shadow">
      <CardHeader className="border-0">
        <h3 className="mb-0">Upload Anki Decks</h3>
        <small className="text-muted">
          Decks are added to the specialty you are currently viewing.
        </small>
      </CardHeader>
      <CardBody>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current && inputRef.current.click()}
          style={{
            border: `2px dashed ${dragActive ? '#5e72e4' : '#dee2e6'}`,
            borderRadius: '8px',
            padding: '32px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragActive ? '#f6f9fc' : 'transparent',
          }}
        >
          <i className="fas fa-cloud-upload-alt fa-2x text-muted mb-2" />
          <p className="mb-0">Drag files here, or click to choose</p>
          <small className="text-muted">
            Allowed: {ALLOWED_RESOURCE_EXTENSIONS.join(', ')}
          </small>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            style={{ display: 'none' }}
            onChange={(e) => addFiles(e.target.files)}
          />
        </div>

        {selected.length > 0 && (
          <div className="mt-4">
            <h5>Selected ({selected.length})</h5>
            {selected.map((entry, index) => {
              const validation = validateResourceFile(entry.file);
              return (
                <div key={fileKey(entry.file)} className="py-3 border-bottom">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <span className={validation.valid ? '' : 'text-danger'}>
                        {entry.file.name}
                      </span>
                      <small className="text-muted ml-2">
                        {formatFileSize(entry.file.size)}
                      </small>
                      {!validation.valid && (
                        <div><small className="text-danger">{validation.error}</small></div>
                      )}
                    </div>
                    <Button close onClick={() => removeFile(index)} />
                  </div>
                  {validation.valid && (
                    <Input
                      type="textarea"
                      rows="2"
                      className="mt-2"
                      placeholder="Description for this deck (optional)"
                      value={entry.description}
                      onChange={(e) => setDescription(index, e.target.value)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {uploadEntries.length > 0 && (
          <div className="mt-3">
            {uploadEntries.map(([id, u]) => (
              <div key={id} className="mb-2">
                <div className="d-flex justify-content-between">
                  <small>{u.fileName}</small>
                  <small className={u.status === 'error' ? 'text-danger' : 'text-muted'}>
                    {u.status === 'error' ? u.error : `${u.progress}%`}
                  </small>
                </div>
                <Progress
                  value={u.progress}
                  color={u.status === 'error' ? 'danger' : u.status === 'done' ? 'success' : 'primary'}
                />
              </div>
            ))}
          </div>
        )}

        {selected.some((entry) => !validateResourceFile(entry.file).valid) && (
          <Alert color="warning" className="mt-3 mb-0">
            Files marked in red will be skipped.
          </Alert>
        )}

        <Button
          color="primary"
          className="mt-3"
          disabled={selected.length === 0 || uploading}
          onClick={handleUpload}
        >
          {uploading ? 'Uploading…' : `Upload ${selected.length || ''} file${selected.length === 1 ? '' : 's'}`}
        </Button>
      </CardBody>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  uploads: state.resourceFiles.uploads,
});

export default connect(mapStateToProps, { uploadResourceFiles, clearResourceUploads })(
  ResourceFileUploader
);
